import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCamera, FiPlay, FiX, FiZoomIn } = FiIcons;

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('birds');

  const galleryItems = {
    birds: [
      { id: 1, title: 'Sri Lanka Blue Magpie', description: 'Morning visit to the garden', type: 'image' },
      { id: 2, title: 'Red-faced Malkoha', description: 'Canopy sighting at Kudawe trail', type: 'image' },
      { id: 3, title: 'Bird Wave in Action', description: 'Mixed-species flock', type: 'video' },
      { id: 4, title: 'Serendib Scops Owl', description: 'Rare daytime roost', type: 'image' },
      { id: 5, title: 'White-faced Starling', description: 'Feeding in the canopy', type: 'image' },
      { id: 6, title: 'Ullinduwawa Border Trail', description: 'Edge habitat exploration', type: 'video' }
    ],
    lodge: [
      { id: 7, title: 'Pool & Garden View', description: 'Relaxation after morning birding', type: 'image' },
      { id: 8, title: 'AC Suite Interior', description: 'Comfort meets nature', type: 'image' },
      { id: 9, title: 'Breakfast Preparation', description: '5:00 AM birder\'s breakfast', type: 'image' },
      { id: 10, title: 'Garden Birding Setup', description: 'Photography hide in action', type: 'image' },
      { id: 11, title: 'Evening at the Lodge', description: 'Sunset from the terrace', type: 'image' },
      { id: 12, title: 'Night Walk Preparation', description: 'Getting ready for owls', type: 'video' }
    ]
  };

  const tabs = [
    { id: 'birds', label: 'Endemic Birds', icon: FiCamera },
    { id: 'lodge', label: 'Lodge Experience', icon: FiPlay }
  ];

  return (
    <section id="gallery" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
            Experience Gallery
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Captured moments from the Kudawe-Ullinduwawa birding experience
          </p>
        </motion.div>

        {/* Gallery Tabs */}
        <div className="flex justify-center mb-12">
          <div className="glass rounded-full p-2 flex space-x-2">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <SafeIcon icon={tab.icon} className="text-lg" />
                <span>{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {galleryItems[activeTab].map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl overflow-hidden hover-lift group cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                <div className="aspect-video bg-gradient-to-br from-emerald-600/30 to-teal-600/30 relative flex items-center justify-center">
                  <div className="text-center">
                    <SafeIcon 
                      icon={item.type === 'video' ? FiPlay : FiCamera} 
                      className="text-4xl text-white/80 mx-auto mb-2" 
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <SafeIcon icon={FiZoomIn} className="text-2xl text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-emerald-300 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 text-sm">{item.description}</p>
                  {item.type === 'video' && (
                    <div className="mt-3">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 py-1 rounded-full text-xs">
                        Video
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="glass rounded-2xl max-w-4xl w-full max-h-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center p-6 border-b border-white/20">
                  <div>
                    <h3 className="text-white text-xl font-semibold">{selectedImage.title}</h3>
                    <p className="text-gray-300">{selectedImage.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="text-white hover:text-emerald-300 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="text-2xl" />
                  </button>
                </div>
                <div className="aspect-video bg-gradient-to-br from-emerald-600/20 to-teal-600/20 flex items-center justify-center">
                  <div className="text-center">
                    <SafeIcon 
                      icon={selectedImage.type === 'video' ? FiPlay : FiCamera} 
                      className="text-8xl text-white/60 mx-auto mb-4" 
                    />
                    <p className="text-white/80 text-lg">{selectedImage.title}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <div className="glass rounded-2xl p-8">
            <h3 className="text-3xl font-bold text-white mb-4 font-playfair">
              Create Your Own Birding Memories
            </h3>
            <p className="text-gray-300 mb-6 text-lg">
              Join us at Jansen's Bungalow and capture the endemic species of Sinharaja 
              in the comfort of our luxury retreat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover-lift"
              >
                Book Photography Package
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="glass text-white px-8 py-4 rounded-full font-semibold text-lg hover-lift border border-white/20"
              >
                Download Full Gallery
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Gallery;